import "./styles.scss";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { useClients } from "context/admin/clients";

import useResponsive from "libs/helpers/useResponsive";
import usePagination from "libs/helpers/usePagination";
import { clients_sorting } from "libs/sorting";
import website_status_data from "libs/website_status_data.json";

import Button from "components/Button";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import ScrollTopMobile from "components/ScrollTopMobile";
import SelectablePill from "components/SelectablePill";
import Select from "components/form/Select";
import PlanTag from "../../../../../client/invoices/Content/components/PlanTag";
import ClientsFilters from "../ClientsFilters";
import ClientsModalFilters from "../ClientsModalFilters";

import { ReactComponent as ViewIcon } from "assets/icons/new/view.svg";
import { ReactComponent as NoDataIcon } from "assets/icons/no-data.svg";

const mainClass = "clients-admin-tiles";

const ClientsTiles = ({ data, loading }) => {
  const { t } = useTranslation();
  const { max } = useResponsive();
  const { search } = useLocation();
  const { filters, filterHandler, filterClearAll } = useClients();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const perPage = max.sm ? 4 : 12;
  const { currentPage, totalPages, handlePageChange, paginatedData } =
    usePagination(data, perPage);

  useEffect(() => {
    if (!max.sm) setIsMobileFiltersOpen(false);
  }, [max.sm]);

  return (
    <div className={mainClass}>
      <div className={`${mainClass}__header`}>
        <div className={`${mainClass}__filters`}>
          {max.sm ? (
            <Button variant="transparent" onClick={() => setIsMobileFiltersOpen(true)}>
              {t("Filter results")}
            </Button>
          ) : (
            <ClientsFilters
              t={t}
              values={filters}
              onChange={(name, value) => filterHandler(name, value, true)}
            />
          )}
          {max.sm && (
            <Button disabled={!filters?.status} onClick={filterClearAll}>
              {t("Clear filters")}
            </Button>
          )}
        </div>
        <div className={`${mainClass}__sort`}>
          <Select.Sort
            key={filters?.sort || "default"}
            initial={filters?.sort}
            options={clients_sorting}
            forceBottom
            onChange={(option) => filterHandler("sort", option.value)}
          />
        </div>
      </div>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={currentPage}
          className={`${mainClass}__row`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "tween", duration: 0.3 }}
        >
          {loading ? (
            <div className={`${mainClass}__loader`}>
              <Loader color="blue" center size="large" />
            </div>
          ) : paginatedData.length > 0 ? (
            paginatedData.map(({ _id, website, tasks, status, subscription }) => {
              const statusData = website_status_data.find((item) => item.value === status);

              return (
                <div className={`${mainClass}__tile`} key={_id}>
                  <h4>{website}</h4>
                  <div className={`${mainClass}__tile-summary`}>
                    <span>{t("Tasks")}</span>
                    <strong>{tasks}</strong>
                  </div>
                  <div className={`${mainClass}__tile-summary`}>
                    <span>{t("Status")}</span>
                    <SelectablePill
                      data={{ label: t(statusData?.label), color: statusData?.color }}
                      isCompletelyDisabled
                    />
                  </div>
                  <div className={`${mainClass}__tile-summary`}>
                    <span>{t("Subscription plan")}</span>
                    {subscription?.[0]?.name ? (
                      <PlanTag name={subscription[0].name} />
                    ) : (
                      <strong>-</strong>
                    )}
                  </div>
                  <Link className={`${mainClass}__tile-action`} to={`/clients/${_id}${search}`}>
                    {t("View")}
                    <ViewIcon aria-hidden="true" />
                  </Link>
                </div>
              );
            })
          ) : (
            <div className={`${mainClass}__no-data`}>
              <NoDataIcon aria-hidden="true" />
              <p>{t("No data")}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      {totalPages > 1 && !loading && (
        <div className={`${mainClass}__pagination`}>
          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={handlePageChange} />
        </div>
      )}
      <div className={`${mainClass}__scroll`}>
        <ScrollTopMobile />
      </div>
      {max.sm && (
        <ClientsModalFilters
          isOpen={isMobileFiltersOpen}
          onClose={() => setIsMobileFiltersOpen(false)}
        />
      )}
    </div>
  );
};

export default ClientsTiles;